import type { BillingStatus } from "./types";

/** A trial gets a banner in its last week. */
export const TRIAL_BANNER_DAYS = 7;

export type BillingBannerKind = "trial" | "bill_ready" | "overdue";

export interface BillingBannerModel {
  kind: BillingBannerKind;
  /** Only an issued bill that is not yet late may be put away for the session. */
  dismissible: boolean;
}

/**
 * Whether the billing banner shows, and which one. Pure, so the rules are
 * testable without rendering:
 *
 * - only for people who can pay (Owners, Admins, super admins) — members see
 *   nothing until the workspace is paused, and then the sign-in screen;
 * - `bill_ready`: a bill is out and the renewal date has not passed;
 * - `overdue`: past the due date, still in grace. `restricted` /
 *   `long_overdue` land here too when nothing is enforcing them (the
 *   platform switch is off, or the token has not caught up), because the bill
 *   is still owed;
 * - `trialing` in its last week.
 */
export function billingBannerFor(status: BillingStatus | null | undefined): BillingBannerModel | null {
  if (!status || !status.can_pay) return null;
  switch (status.stage) {
    case "bill_ready":
      return status.statement ? { kind: "bill_ready", dismissible: true } : null;
    case "overdue":
    case "restricted":
    case "long_overdue":
      // A trial that just ended can be overdue before its first bill is
      // issued (the lifecycle runs hourly) — still worth saying.
      return { kind: "overdue", dismissible: false };
    case "trialing":
      return status.days_left != null && status.days_left <= TRIAL_BANNER_DAYS
        ? { kind: "trial", dismissible: false }
        : null;
    default:
      return null;
  }
}

/**
 * Format an amount the server worked out. Formatting only — `total` is shown
 * as given, never recomputed.
 */
export function formatMoney(total: string, currency: string, locale?: string): string {
  const value = Number(total);
  if (!Number.isFinite(value)) return `${total} ${currency}`;
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(value);
  } catch {
    return `${total} ${currency}`;
  }
}

export function formatDay(iso: string | null | undefined, locale?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
}
